import { useCallback, useEffect, useRef } from 'react';
import type { Dispatch } from 'react';
import type { GestureState, HtmlViewerAction } from '../HtmlViewerTypes';

const LONG_PRESS_MS = 350;
const TOUCH_MOVE_THRESHOLD_PX = 8;

// ─── Cross-browser caret API ──────────────────────────────────────────────────

function caretRangeFromPoint(x: number, y: number): Range | null {
  if (typeof document === 'undefined') return null;

  if (document.caretRangeFromPoint) {
    return document.caretRangeFromPoint(x, y); // Chrome, Safari
  }

  // Firefox
  const pos = (document as Document & {
    caretPositionFromPoint?: (x: number, y: number) => { offsetNode: Node; offset: number } | null;
  }).caretPositionFromPoint?.(x, y);

  if (!pos) return null;

  const range = document.createRange();
  range.setStart(pos.offsetNode, pos.offset);
  range.collapse(true);
  return range;
}

function isNodeInsideContent(node: Node, contentRoot: HTMLElement | null): boolean {
  return Boolean(contentRoot && contentRoot.contains(node));
}

interface UseGestureStateMachineOptions {
  dispatch: Dispatch<HtmlViewerAction>;
  isHighlightMode: () => boolean;
  contentRef: React.RefObject<HTMLElement>;
  surfaceRef: React.RefObject<HTMLElement>;
}

interface GestureHandlers {
  onPointerDown: (e: React.PointerEvent) => void;
  onPointerMove: (e: React.PointerEvent) => void;
  onPointerUp: (e: React.PointerEvent) => void;
  onPointerCancel: (e: React.PointerEvent) => void;
}

export function useGestureStateMachine({
  dispatch,
  isHighlightMode,
  contentRef,
  surfaceRef,
}: UseGestureStateMachineOptions): GestureHandlers {
  const gestureRef = useRef<GestureState>({ kind: 'idle' });
  const longPressTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const clearLongPressTimer = useCallback(() => {
    if (longPressTimerRef.current !== null) {
      clearTimeout(longPressTimerRef.current);
      longPressTimerRef.current = null;
    }
  }, []);

  const clearNativeSelection = useCallback(() => {
    window.getSelection()?.removeAllRanges();
  }, []);

  const finishGesture = useCallback(() => {
    clearLongPressTimer();
    clearNativeSelection();
    gestureRef.current = { kind: 'idle' };
  }, [clearLongPressTimer, clearNativeSelection]);

  const startGesture = useCallback(
    (clientX: number, clientY: number, pointerType: string) => {
      if (!isHighlightMode()) return;

      gestureRef.current = {
        kind: 'pressing',
        startX: clientX,
        startY: clientY,
        startTime: Date.now(),
        pointerType,
      };

      if (pointerType !== 'mouse') {
        longPressTimerRef.current = setTimeout(() => {
          if (gestureRef.current.kind !== 'pressing') return;
          const { startX, startY } = gestureRef.current;
          gestureRef.current = { kind: 'longPressArmed', startX, startY };
          clearNativeSelection();
        }, LONG_PRESS_MS);
      }
    },
    [clearNativeSelection, isHighlightMode]
  );

  const updateSelection = useCallback(
    (anchorRange: Range, clientX: number, clientY: number) => {
      const endRange = caretRangeFromPoint(clientX, clientY);
      if (!endRange) return;
      const contentRoot = contentRef.current;
      if (!contentRoot) return;
      if (
        !isNodeInsideContent(anchorRange.startContainer, contentRoot) ||
        !isNodeInsideContent(endRange.startContainer, contentRoot)
      ) {
        return;
      }

      const sel = window.getSelection();
      if (!sel) return;

      try {
        const liveRange = document.createRange();
        const cmp = anchorRange.compareBoundaryPoints(
          Range.START_TO_START,
          endRange
        );

        if (cmp <= 0) {
          liveRange.setStart(anchorRange.startContainer, anchorRange.startOffset);
          liveRange.setEnd(endRange.startContainer, endRange.startOffset);
        } else {
          liveRange.setStart(endRange.startContainer, endRange.startOffset);
          liveRange.setEnd(anchorRange.startContainer, anchorRange.startOffset);
        }

        sel.removeAllRanges();
        sel.addRange(liveRange);
      } catch {
        // Ignore invalid boundary transitions.
      }
    },
    [contentRef]
  );

  const moveGesture = useCallback(
    (clientX: number, clientY: number) => {
      const gs = gestureRef.current;

      if (gs.kind === 'pressing') {
        const dx = Math.abs(clientX - gs.startX);
        const dy = Math.abs(clientY - gs.startY);

        if (gs.pointerType === 'mouse') {
          if (dx > 2 || dy > 2) {
            clearLongPressTimer();
            const anchorRange = caretRangeFromPoint(gs.startX, gs.startY);
            if (!anchorRange) return;
            gestureRef.current = { kind: 'selecting', anchorRange };
            updateSelection(anchorRange, clientX, clientY);
          }
        } else if (dx > TOUCH_MOVE_THRESHOLD_PX || dy > TOUCH_MOVE_THRESHOLD_PX) {
          clearLongPressTimer();
          // strict 정책: 터치 스크롤 대신 선택 모드 유지(gesture 취소)
          gestureRef.current = { kind: 'scrolling' };
        }
        return;
      }

      if (gs.kind === 'longPressArmed') {
        const anchorRange = caretRangeFromPoint(clientX, clientY);
        if (!anchorRange) return;
        gestureRef.current = { kind: 'selecting', anchorRange };
        updateSelection(anchorRange, clientX, clientY);
        return;
      }

      if (gs.kind === 'selecting') {
        updateSelection(gs.anchorRange, clientX, clientY);
      }
    },
    [clearLongPressTimer, updateSelection]
  );

  const commitGesture = useCallback(
    (clientX: number, clientY: number) => {
      const gs = gestureRef.current;

      if (gs.kind === 'selecting') {
        const sel = window.getSelection();
        if (sel && !sel.isCollapsed && sel.rangeCount > 0) {
          const range = sel.getRangeAt(0);
          if (!range.collapsed) {
            dispatch({ type: 'SET_PENDING_RANGE', payload: range.cloneRange() });
            dispatch({
              type: 'SET_MENU_POSITION',
              payload: { x: clientX, y: clientY },
            });
            dispatch({ type: 'SET_ACTIVE_ANNOTATION', payload: null });
          }
        }
      }

      finishGesture();
    },
    [dispatch, finishGesture]
  );

  const onPointerDown = useCallback(
    (e: React.PointerEvent) => {
      if (!isHighlightMode()) return;
      e.preventDefault();
      if (e.currentTarget.setPointerCapture) {
        e.currentTarget.setPointerCapture(e.pointerId);
      }
      startGesture(e.clientX, e.clientY, e.pointerType);
    },
    [isHighlightMode, startGesture]
  );

  const onPointerMove = useCallback(
    (e: React.PointerEvent) => {
      if (!isHighlightMode()) return;
      e.preventDefault();
      moveGesture(e.clientX, e.clientY);
    },
    [isHighlightMode, moveGesture]
  );

  const onPointerUp = useCallback(
    (e: React.PointerEvent) => {
      if (!isHighlightMode()) return;
      e.preventDefault();
      if (e.currentTarget.releasePointerCapture) {
        e.currentTarget.releasePointerCapture(e.pointerId);
      }
      commitGesture(e.clientX, e.clientY);
    },
    [commitGesture, isHighlightMode]
  );

  const onPointerCancel = useCallback(() => {
    if (!isHighlightMode()) return;
    finishGesture();
  }, [finishGesture, isHighlightMode]);

  useEffect(() => {
    if (isHighlightMode()) return;
    finishGesture();
  }, [finishGesture, isHighlightMode]);

  useEffect(() => {
    return () => {
      clearLongPressTimer();
    };
  }, [clearLongPressTimer]);

  // Suppress native selection UI while highlight mode is active.
  useEffect(() => {
    if (typeof document === 'undefined') return undefined;

    const handleSelectionChange = () => {
      if (!isHighlightMode()) return;
      if (gestureRef.current.kind === 'selecting') return;
      const sel = window.getSelection();
      if (sel && !sel.isCollapsed) {
        sel.removeAllRanges();
      }
    };

    document.addEventListener('selectionchange', handleSelectionChange);
    return () => {
      document.removeEventListener('selectionchange', handleSelectionChange);
    };
  }, [isHighlightMode]);

  // Touch fallback for environments without Pointer Events.
  useEffect(() => {
    if (typeof window === 'undefined') return undefined;
    if ('PointerEvent' in window) return undefined;

    const surface = surfaceRef.current;
    if (!surface) return undefined;

    const firstTouch = (event: TouchEvent): Touch | null => {
      if (event.touches.length > 0) return event.touches[0];
      if (event.changedTouches.length > 0) return event.changedTouches[0];
      return null;
    };

    const onTouchStart = (event: TouchEvent) => {
      if (!isHighlightMode() || event.touches.length !== 1) return;
      const touch = firstTouch(event);
      if (!touch) return;
      event.preventDefault();
      startGesture(touch.clientX, touch.clientY, 'touch');
    };

    const onTouchMove = (event: TouchEvent) => {
      if (!isHighlightMode()) return;
      const touch = firstTouch(event);
      if (!touch) return;
      event.preventDefault();
      moveGesture(touch.clientX, touch.clientY);
    };

    const onTouchEnd = (event: TouchEvent) => {
      if (!isHighlightMode()) return;
      const touch = firstTouch(event);
      if (!touch) {
        finishGesture();
        return;
      }
      event.preventDefault();
      commitGesture(touch.clientX, touch.clientY);
    };

    const onTouchCancel = () => {
      if (!isHighlightMode()) return;
      finishGesture();
    };

    surface.addEventListener('touchstart', onTouchStart, { passive: false });
    surface.addEventListener('touchmove', onTouchMove, { passive: false });
    surface.addEventListener('touchend', onTouchEnd, { passive: false });
    surface.addEventListener('touchcancel', onTouchCancel, { passive: false });

    return () => {
      surface.removeEventListener('touchstart', onTouchStart);
      surface.removeEventListener('touchmove', onTouchMove);
      surface.removeEventListener('touchend', onTouchEnd);
      surface.removeEventListener('touchcancel', onTouchCancel);
    };
  }, [
    commitGesture,
    finishGesture,
    isHighlightMode,
    moveGesture,
    startGesture,
    surfaceRef,
  ]);

  return { onPointerDown, onPointerMove, onPointerUp, onPointerCancel };
}
